import React from 'react';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import { UserRole } from '../../store/authStore';

// Struktura menu (analiza UX, propozycja 1): kilka stalych pozycji na co dzien
// i zwijane grupy. Wszystkie adresy stron zostaja bez zmian — zmienia sie
// tylko to, gdzie pozycja lezy w menu. Uprawnienia sa kopia rol z App.tsx.

export interface NavLink {
  label: string;
  path: string;
  roles: UserRole[];
  /** Dodatkowe adresy, pod ktorymi pozycja ma byc zaznaczona. */
  match?: RegExp[];
  /** Slowa kluczowe dla wyszukiwarki (Ctrl+K). */
  keywords?: string;
}

export interface NavPinned extends NavLink {
  icon: React.ReactNode;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: NavLink[];
}

const ALL_STAFF_ROLES: UserRole[] = ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'STAFF', 'ACCOUNTANT', 'ANALYST', 'MARKETER', 'IT'];
const TIME_ROLES: UserRole[] = ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER'];

export const NAV_PINNED: NavPinned[] = [
  { label: 'Panel', path: '/dashboard', icon: <SpaceDashboardOutlinedIcon />, roles: [...ALL_STAFF_ROLES, 'CLIENT'], keywords: 'pulpit start kokpit dzisiaj' },
  { label: 'Kalendarz', path: '/salon/calendar', icon: <CalendarMonthOutlinedIcon />, roles: ['SALON_OWNER', 'STAFF'], keywords: 'wizyty rezerwacje grafik terminy' },
  { label: 'Sprzedaż', path: '/salon/sales', icon: <PointOfSaleOutlinedIcon />, roles: ['SALON_OWNER', 'STAFF'], keywords: 'kasa paragon transakcje płatność' },
  { label: 'Klienci', path: '/salon/clients', icon: <PeopleAltOutlinedIcon />, roles: ['SALON_OWNER', 'STAFF', 'MARKETER'], keywords: 'kartoteka baza klientek' },
  { label: 'Zadania', path: '/tasks', icon: <TaskAltOutlinedIcon />, roles: ALL_STAFF_ROLES, keywords: 'todo obowiązki' },
  // Klient
  { label: 'Znajdź salon', path: '/salons', icon: <StorefrontOutlinedIcon />, roles: ['CLIENT'], match: [/^\/salons\/[^/]+$/, /^\/booking$/], keywords: 'salony rezerwacja' },
  { label: 'Moje wizyty', path: '/bookings/my', icon: <EventNoteOutlinedIcon />, roles: ['CLIENT'], keywords: 'rezerwacje terminy' },
  { label: 'Mój profil', path: '/profile', icon: <PersonOutlineOutlinedIcon />, roles: ['CLIENT'], keywords: 'konto dane' },
];

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'admin',
    label: 'Administracja',
    icon: <AdminPanelSettingsOutlinedIcon />,
    items: [
      { label: 'Salony w systemie', path: '/admin/salons', roles: ['SUPER_ADMIN'], keywords: 'administracja' },
      { label: 'Statystyki platformy', path: '/admin/stats', roles: ['SUPER_ADMIN'] },
      { label: 'Katalog salonów', path: '/salons', roles: ['SUPER_ADMIN'], match: [/^\/salons\/.+/] },
    ],
  },
  {
    id: 'team',
    label: 'Zespół',
    icon: <BadgeOutlinedIcon />,
    items: [
      { label: 'Pracownicy', path: '/salon/staff', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'ACCOUNTANT'], match: [/^\/employees/], keywords: 'kartoteka profil pracownika zespół' },
      { label: 'Dzisiejsza obecność', path: '/time/today', roles: TIME_ROLES, keywords: 'kto jest w pracy' },
      { label: 'Czas pracy', path: '/time/dashboard', roles: TIME_ROLES, keywords: 'godziny ewidencja' },
      { label: 'Kalendarz pracy', path: '/time/calendar', roles: TIME_ROLES, keywords: 'grafik zmiany' },
      { label: 'Korekty czasu', path: '/time/adjustments', roles: TIME_ROLES, keywords: 'poprawki wniosek' },
      { label: 'Nieobecności i L4', path: '/time/absences', roles: TIME_ROLES, keywords: 'urlop choroba zwolnienie' },
    ],
  },
  {
    id: 'payroll',
    label: 'Wynagrodzenia',
    icon: <AccountBalanceWalletOutlinedIcon />,
    items: [
      { label: 'Rozliczenia miesięczne', path: '/payroll/periods', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'ACCOUNTANT'], keywords: 'wypłaty pensje' },
      { label: 'Zasady wynagrodzeń', path: '/payroll/rules', roles: ['SUPER_ADMIN', 'SALON_OWNER'], keywords: 'stawki prowizje' },
      { label: 'Symulator wynagrodzeń', path: '/payroll/simulator', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER'] },
      { label: 'Ustawienia rozliczania', path: '/time/settings', roles: ['SUPER_ADMIN', 'SALON_OWNER'], keywords: 'zaokrąglanie czas rozliczeniowy' },
    ],
  },
  {
    id: 'services',
    label: 'Usługi i magazyn',
    icon: <ContentCutOutlinedIcon />,
    items: [
      { label: 'Usługi i cennik', path: '/salon/services', roles: ['SALON_OWNER', 'STAFF'], keywords: 'ceny oferta zabiegi' },
      { label: 'Magazyn', path: '/salon/inventory', roles: ['SALON_OWNER'], match: [/^\/salon\/[^/]+\/inventory/], keywords: 'stany' },
      { label: 'Produkty', path: '/salon/products', roles: ['SALON_OWNER'], match: [/^\/salon\/[^/]+\/products/], keywords: 'kosmetyki' },
      { label: 'Inwentaryzacja', path: '/salon/audits', roles: ['SALON_OWNER'], match: [/^\/salon\/[^/]+\/audits/], keywords: 'spis z natury' },
      { label: 'Marki i oddziały', path: '/salon/brands', roles: ['SALON_OWNER'], keywords: 'lokalizacje przesunięcia' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: <CampaignOutlinedIcon />,
    items: [
      { label: 'Kampanie', path: '/salon/marketing', roles: ['SALON_OWNER', 'MARKETER'], keywords: 'marketing sms e-mail' },
      { label: 'Karty i pakiety', path: '/salon/gifts', roles: ['SALON_OWNER', 'MARKETER'], keywords: 'karty podarunkowe vouchery' },
    ],
  },
  {
    id: 'analytics',
    label: 'Analizy',
    icon: <InsightsOutlinedIcon />,
    items: [
      { label: 'Statystyki salonu', path: '/salon/stats', roles: ['SALON_OWNER'], keywords: 'raporty przychody' },
      { label: 'Rentowność', path: '/profitability', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'ANALYST'], keywords: 'zysk marża koszty' },
      { label: 'Statystyki zadań', path: '/tasks/stats', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'STAFF'] },
    ],
  },
  {
    id: 'me',
    label: 'Moje sprawy',
    icon: <PersonOutlineOutlinedIcon />,
    items: [
      { label: 'Mój czas pracy', path: '/me/time', roles: ['STAFF', 'MANAGER', 'SALON_OWNER'], keywords: 'panel pracownika wejście wyjście' },
      { label: 'Moje nieobecności', path: '/me/absences', roles: ['STAFF', 'MANAGER', 'SALON_OWNER'], keywords: 'urlop wniosek' },
      { label: 'Moje wypłaty', path: '/me/payroll', roles: ['STAFF', 'MANAGER', 'SALON_OWNER'] },
    ],
  },
  {
    id: 'settings',
    label: 'Ustawienia salonu',
    icon: <TuneOutlinedIcon />,
    items: [
      { label: 'Profil biznesu', path: '/salon/profile', roles: ['SALON_OWNER', 'IT'], match: [/^\/salon\/settings$/], keywords: 'dane adres godziny otwarcia logo' },
      { label: 'Wizytówka WWW', path: '/salon/website', roles: ['SALON_OWNER', 'IT'], keywords: 'strona rezerwacja online link' },
      { label: 'Płatności', path: '/salon/payments', roles: ['SALON_OWNER'], keywords: 'stripe metody płatności' },
      { label: 'Użytkownicy i role', path: '/salon/users', roles: ['SALON_OWNER'], keywords: 'dostęp uprawnienia zaproszenia' },
      { label: 'Subskrypcja', path: '/salon/subscription', roles: ['SALON_OWNER'], keywords: 'plan faktury' },
    ],
  },
];

export interface ResolvedNav {
  pinned: NavPinned[];
  /** Grupy z co najmniej dwiema pozycjami dla tej roli. */
  groups: NavGroup[];
  /** Pozycje z grup, w ktorych rola ma tylko jedna pozycje — pokazywane jako zwykle. */
  singles: NavPinned[];
}

export const resolveNav = (role: UserRole | undefined): ResolvedNav => {
  if (!role) return { pinned: [], groups: [], singles: [] };
  const pinned = NAV_PINNED.filter((i) => i.roles.includes(role));
  const groups: NavGroup[] = [];
  const singles: NavPinned[] = [];
  NAV_GROUPS.forEach((g) => {
    const items = g.items.filter((i) => i.roles.includes(role));
    if (items.length >= 2) groups.push({ ...g, items });
    else if (items.length === 1) singles.push({ ...items[0], icon: g.icon });
  });
  return { pinned, groups, singles };
};

const matches = (pathname: string, link: NavLink) =>
  pathname === link.path || pathname.startsWith(`${link.path}/`) || (link.match || []).some((re) => re.test(pathname));

/** Najdluzej pasujaca pozycja — /tasks/stats nie zaznacza „Zadania”. */
export const findActivePath = (pathname: string, nav: ResolvedNav): { path: string | null; groupId: string | null } => {
  let best: { path: string; groupId: string | null; score: number } | null = null;
  const consider = (link: NavLink, groupId: string | null) => {
    if (!matches(pathname, link)) return;
    const score = pathname === link.path ? 10000 : link.path.length;
    if (!best || score > best.score) best = { path: link.path, groupId, score };
  };
  nav.pinned.forEach((l) => consider(l, null));
  nav.singles.forEach((l) => consider(l, null));
  nav.groups.forEach((g) => g.items.forEach((l) => consider(l, g.id)));
  const b = best as { path: string; groupId: string | null } | null;
  return { path: b?.path ?? null, groupId: b?.groupId ?? null };
};

export const ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: 'Administrator',
  SALON_OWNER: 'Właściciel',
  MANAGER: 'Menedżer',
  STAFF: 'Pracownik',
  ACCOUNTANT: 'Księgowość',
  ANALYST: 'Analityk',
  MARKETER: 'Marketing',
  IT: 'Informatyk',
  CLIENT: 'Klient',
};
